import { IpaType } from "../phonemes/ipa";
import { compileRuleSet } from "../rules/compileRuleSet";
import { SegmentStrategy } from "../rules/intoSegments";
import { PhoneLengths } from "../rules/lengthRules";
import { normIpa } from "../rules/normIpa";
import { RuleSet } from "../rules/ruleSet";
import { FailedRule } from "../rules/rulesRunner";
import { runRulesOnIpa } from "../rules/runRules";
import { convertPlusSymbols } from "./convertPlusSymbols";
import { workaround } from "./workaround";

/** convert IPA into phonemes used by the speech synthesizer,
 * return synth, unsupported IPA (error in input), unsupported synth (error in conversion rules) */
export function ipaToSynth(ipa: IpaType, addLengths?: PhoneLengths)
  : { speak: string, unsupported?: string[], badSynth?: string[] } {

  let processed = normIpa(ipa);  // so there's a single consistent way to specify info
  preprocess.forEach(map => { processed = processed.replace(map.src, map.dest) });
  processed = workaround(processed);  // deal w/ espeak bugs
  let unsupported: string[] | undefined;

  // optionally add on phoneme length modifiers
  if (addLengths) {
    const results2 = runRulesOnIpa(processed, addLengths.rules);
    processed = results2.ipa ? results2.ipa : processed;
    unsupported = results2.hasUnknowns?.filter(p => p.meta === 'unknown').map(p => p.phoneme);
  }
  // e.g., milliseconds -> synth lengths
  const withLengths = convertPlusSymbols(processed, addLengths?.defaultDiphthong);

  // IPA -> espeak
  const results = runRulesOnIpa(withLengths, ipaCompiled);
  processed = results.ipa ? results.ipa : ipa;
  if (unsupported === undefined) {
    unsupported = results.hasUnknowns?.filter(p => p.meta === 'unknown').map(p => p.phoneme);
  }

  const badChars = /([^abcdefghijklmnopqrstuvwxyzABCDEIJKLNOPQRSTUVXYZ0123456789&*@?!#~:;'"`_|[\-+/^., ])/g;
  const notfound = processed.match(badChars);
  processed = processed.replace(badChars, '');
  const badSynth = notfound ? notfound.map(v => v) : undefined;

  postprocess.forEach(map => { processed = processed.replace(map.src, map.dest) });
  return { speak: processed, unsupported, badSynth };
}

export function getConversionRuleErrors(): FailedRule[] {
  return ipaFailed;
}

// search-and-replace to do before & after running rules
const preprocess = [
  { src: /^\s*\//g, dest: '' },
  { src: /\/\s*$/g, dest: '' },
  // convert here since /ɹ/ has complex rules
  { src: /ɚː/g, dest: 'əːɹ' }, { src: /ɚˑ/g, dest: 'əˑɹ' }, { src: /ɚ\u0306/g, dest: 'ə\u0306ɹ' }, { src: /ɚ/g, dest: 'əɹ' },
  { src: /ʴ/g, dest: 'ɹ' }, { src: /˞/g, dest: 'ɹ' },
  // turn phoneme delimiters into pauses
  { src: /\/|\[|\]|⟦|⟧/g, dest: ' . ' },
  // remove lengthener on tones
  { src: /([(\u02E5)(\u02E6)(\u02E7)(\u02E8)(\u02E9)])ː/g, dest: '$1' },
  // convert tone diacritics to supported combos of the five tones
  { src: /\u030F/g, dest: '˩' },  // e.g., ȅ
  { src: /\u0300/g, dest: '˨' },  // e.g., è
  { src: /\u0304/g, dest: '˧' },  // e.g., ē
  { src: /\u0301/g, dest: '˦' },  // e.g., é
  { src: /\u030B/g, dest: '˥' },  // e.g., e̋
  { src: /\u030c/g, dest: '˩˥' },  // e.g., ě
  { src: /\u0302/g, dest: '˥˩' },  // e.g., ê
  { src: /\u1dc4/g, dest: '˨˦' },  // e.g., e᷄   ˧˥
  { src: /\u1dc5/g, dest: '˨˦' },  // e.g., e᷅   ˩˧
  { src: /\u1dc6/g, dest: '˦˨' },  // e.g., ◌᷆   ˧˩
  { src: /\u1dc7/g, dest: '˦˨' },  // e.g., ◌᷇   ˥˧
  { src: /\u1dc8/g, dest: '˨˦˨' },  // e.g., e᷈   ˩˥˩
  { src: /\u1dc9/g, dest: '˦˨˦' },  // e.g., ◌᷉   ˥˩˥
];
const postprocess = [
  // to signal a pause, put spaces around the punctuation
  { src: / , /g, dest: ' _, ' },
  { src: / \. /g, dest: ' _. ' }, { src: / \.$/g, dest: ' _.' },
  // placeholders for speakPhonemes to use
  { src: / \? /g, dest: ' _? ' }, { src: / \?$/g, dest: ' _?' },
  { src: / ! /g, dest: ' _! ' }, { src: / !$/g, dest: ' _!' },
];

// a RuleSet works better than search-and-replace
// because later rules don't change an earlier result
const ipaRules: RuleSet = {
  preprocess: ['cleanPunctuation'],
  inputSegments: SegmentStrategy.IpaPlus,
  outputSegments: SegmentStrategy.SynthSymbols,
  subsetMatches: true,  // if no exact match, try matching a subset of the input string to deal w/ unhandled diacritics
  diacriticRules: [
    // split on tie bars, zero-width-joiner
    { diacritics: ['\u035C', '\u0361', '\u200D', '\u203f'], to: '', split: true },
    // syllabic consonants
    { diacritics: ['\u030D', '\u0329'], to: '-:', mustBe: ['long'] },
    { diacritics: ['\u030D', '\u0329'], to: '-' },
    // turn an unhandled non-syllabic vowel into a short vowel
    { diacritics: ['\u032fː'], toPre: ':', to: '0' }, // lengthen previous vowel
    { diacritics: ['\u032f'], to: '0' },
    // any lengthening not explicitly handled
    { diacritics: ['ːː'], to: '9' }, // extra long
    { diacritics: ['ː'], to: ':' }, // long
    { diacritics: ['ˑ'], to: '2' }, // half-long
    { diacritics: ['\u0306'], to: '0' }, // extra-short
    // unhandled ejectives faked with a glottal stop
    { diacritics: ['ʼ'], to: '?' },
  ],

  rules: [

    // special case of modifier followed by length, convert & swap
    { from: 'ːː', to: '9', previous: ['modifier'], post: 'swapWithPrev' },
    { from: 'ː', to: ':', previous: ['modifier'], post: 'swapWithPrev' },
    { from: 'ˑ', to: '2', previous: ['modifier'], post: 'swapWithPrev' },
    { from: '\u0306', to: '0', previous: ['modifier'], post: 'swapWithPrev' },

    // drop syllable & morpheme breaks
    { from: '.', to: '' },
    { from: '-', to: '' },

    // rules that check surrounding phonemes, therefore should be run earlier
    { from: 'ŋk', to: 'Nk/0', next: { or: [":plosive", ":affricate", ":fricative"] } }, // short [Nk]
    { from: 'k', to: 'k/0', previous: ['consonant'], next: { or: [":plosive", ":affricate", ":fricative"] } },  // short [k]
    { from: 'p', to: 'p/0', next: { or: [":plosive", ":affricate", ":fricative"] } },  // short [p]
    // faking labialization
    { from: 'ʷʰ', to: 'w#' }, { from: 'ʰʷ', to: 'w#' },  // [ʍ]
    { from: 'ʷ', to: 'w#', next: ['#word'] },
    { from: 'ʷ', to: 'w#', previous: ['consonant', 'voiceless'], next: ['consonant', 'voiceless'] }, // [ʍ]
    { from: 'ʷ', to: 'u0@-', previous: ['consonant'], next: ['consonant'] },  // [ŭə̆]
    { from: 'ʷ', to: 'w' },

    // palatalization works before a vowel and after some consonants
    { from: 'sʲ', to: 'sj', next: ['vowel'] }, { from: 'zʲ', to: 'zj', next: ['vowel'] }, // sounds better than ;
    { from: 'ʲ', to: ';', next: ['vowel'] },
    { from: 'ʲ', to: ';', previous: { or: ['/t/', '/k/', '/r/', '/s/', '/z/', '/ʃ/', '/ʒ/', '/tʃ/', '/dʒ/', '/t͡ʃ/', '/d͡ʒ/'] } },
    // after a vowel, use i̯
    { from: 'ʲ', to: 'i0', previous: ['vowel'] },
    // approximated palatalization
    { from: 'n̪ʲ', to: 'n^', next: { not: ':vowel' } }, { from: 'n̠ʲ', to: 'n^', next: { not: ':vowel' } },
    { from: 'nʲ', to: 'n^', next: { not: ':vowel' } },  // nasals > ɲ
    { from: 'ɳʲ', to: 'n^', next: { not: ':vowel' } }, { from: 'ȵʲ', to: 'n^', next: { not: ':vowel' } },
    { from: 'ŋʲ', to: 'n^', next: { not: ':vowel' } }, { from: 'ɴʲ', to: 'n^', next: { not: ':vowel' } },
    { from: 'l̪ʲ', to: 'l^', next: { not: ':vowel' } }, { from: 'ʟʲ', to: 'l^', next: { not: ':vowel' } },
    { from: 'lʲ', to: 'l^', next: { not: ':vowel' } },  // laterals > ʎ
    { from: 'ɭʲ', to: 'l^', next: { not: ':vowel' } }, { from: 'ȴʲ', to: 'l^', next: { not: ':vowel' } },
    // otherwise drop it so it's easier to tell when it's unsupported
    { from: 'ʲ', to: '' },

    // rhotics
    { from: "ˈɹ", to: "'r" }, { from: "ˌɹ", to: ",r" }, // don't merge rhotics across a stress boundary
    { from: 'ɝː', to: 'P:' }, { from: 'ɝ', to: 'P' }, { from: 'ɝˑ', to: 'P3' },
    { from: 'ɜːɹ', to: 'P:' }, { from: 'ɜɹ', to: 'P' },
    { from: 'ɹ̩ː', to: 'P:' }, { from: 'ɹ̩', to: 'P' },
    { from: 'ɑːɹ', to: 'A+r:' }, { from: 'ɑɹ', to: 'A+r' },
    { from: 'e̞ːɹ', to: 'E+r:' }, { from: 'e̞ɹ', to: 'E+r' },
    { from: 'ɪːɹ', to: 'I+r:' }, { from: 'ɪɹ', to: 'I+r' },
    { from: 'oːɹ', to: 'o+r:' }, { from: 'oɹ', to: 'o+r' },
    { from: 'ɤːɹ', to: 'U+r:' }, { from: 'ɤɹ', to: 'U+r' },
    { from: 'ɹ', to: 'P0', previous: ['vowel'], next: { not: ':vowel' } },
    { from: 'ɹ', to: 'P0', previous: { seq: [':vowel', ':tone'] }, next: { not: ':vowel' } },
    { from: 'ɹ', to: 'P0', previous: { seq: [':length', ':tone'] }, next: { not: ':vowel' } },

    // exact diphthongs (and close ones, also listed in ipaToSynthSymbols)
    { from: 'ɐʉ̯', to: '&+u' }, { from: 'ɐʉ̯ː', to: '&+u:' },
    { from: 'ɛə̯', to: 'E+@' }, { from: 'ɛə̯ː', to: 'E+@:' },
    { from: 'əʊ̯', to: '@+U' }, { from: 'əʊ̯ː', to: '@+U:' },
    { from: 'ɯu̯', to: 'u.' },
    // approximate
    { from: 'ia̯u̯', to: 'jaw' },

    // non-syllabic vowels used as first part of a diphthong
    { from: 'i̯', to: 'j', previous: { not: ':vowel' }, next: ['vowel'] },
    { from: 'ɪ̯', to: 'j', previous: { not: ':vowel' }, next: ['vowel'] },
    { from: 'ɨ̯', to: 'y^', previous: { not: ':vowel' }, next: ['vowel'] },
    { from: 'y̯', to: 'y^', previous: { not: ':vowel' }, next: ['vowel'] },
    { from: 'ʉ̯', to: 'w^', previous: { not: ':vowel' }, next: ['vowel'] },
    { from: 'ɯ̯', to: 'w^', previous: { not: ':vowel' }, next: ['vowel'] },
    { from: 'u̯', to: 'w', previous: { not: ':vowel' }, next: ['vowel'] },
    { from: 'ʊ̯', to: 'w', previous: { not: ':vowel' }, next: ['vowel'] },
    { from: 'ɵ̯', to: 'w', previous: { not: ':vowel' }, next: ['vowel'] },
    { from: 'ə̯', to: '@-', previous: { not: ':vowel' }, next: ['vowel'] },
    { from: 'ɞ̯', to: '@-', previous: { not: ':vowel' }, next: ['vowel'] },
    { from: 'ɐ̯', to: '@-', previous: { not: ':vowel' }, next: ['vowel'] },
    { from: 'ʌ̯', to: '@-', previous: { not: ':vowel' }, next: ['vowel'] },
    { from: 'ɝ̯', to: 'P3', next: ['vowel'] },

    // nasals
    { from: 'ã', to: 'a~' }, { from: 'aⁿ', to: 'a~' },
    { from: 'æ̃', to: '&~' }, { from: 'æⁿ', to: '&~' },
    { from: 'ɐ̃', to: '&^~' }, { from: 'ɐⁿ', to: '&^~' },
    { from: 'ẽ', to: 'e~' }, { from: 'eⁿ', to: 'e~' },
    { from: 'ɛ̃', to: 'E~' }, { from: 'ɛⁿ', to: 'E~' },
    { from: 'ĩ', to: 'i~' }, { from: 'iⁿ', to: 'i~' },
    { from: 'ɪ̃', to: 'I~' }, { from: 'ɪⁿ', to: 'I~' },
    { from: 'õ', to: 'o~' }, { from: 'oⁿ', to: 'o~' },
    { from: 'ɔ̃', to: 'O~' }, { from: 'ɔⁿ', to: 'O~' },
    { from: 'ũ', to: 'u~' }, { from: 'uⁿ', to: 'u~' },
    { from: 'ʌ̃', to: 'V~' }, { from: 'ʌⁿ', to: 'V~' },
    { from: 'ə̃', to: '@~' }, { from: 'əⁿ', to: '@~' },

    // IPA vowels
    { from: 'ä', to: 'a#' }, { from: 'ɐ̞', to: 'a#' },
    { from: 'a', to: 'a' },
    { from: 'æ', to: '&' },
    { from: 'ɐ', to: '&^' },
    { from: 'ɑ̝', to: 'A^' },
    { from: 'ɑ̈', to: 'A#' },
    { from: 'ɑ', to: 'A' },
    { from: 'ɒ', to: 'A.' },
    { from: 'ɶ', to: 'a.' },
    { from: 'e̞', to: 'e-' },
    { from: 'e', to: 'e' },
    { from: 'ɘ', to: 'e^' },
    { from: 'ɛ', to: 'E' },
    { from: 'œ', to: 'E.' },
    { from: 'ɜ', to: 'E^' },
    { from: 'ø̞', to: 'e#' },
    { from: 'ø', to: 'e.' },
    { from: 'i', to: 'i' },
    { from: 'ɨ', to: 'i^' },
    { from: 'ɪ', to: 'I' },
    { from: 'o̞', to: 'o-' },
    { from: 'o̝', to: 'o^' },
    { from: 'o', to: 'o' },
    { from: 'ɵ', to: 'o#' },
    { from: 'ɔ', to: 'O' },
    { from: 'ɞ', to: 'O^' },
    { from: 'ʉ', to: 'u^' },
    { from: 'u̠', to: 'u#' },
    { from: 'ŭ', to: 'w' },
    { from: 'u', to: 'u' },
    { from: 'ɯ', to: 'u-' },
    { from: 'ɤ̞', to: 'U#' },
    { from: 'ɤ', to: 'U.' },
    { from: 'ʊ̞', to: 'U-' },
    { from: 'ʊ', to: 'U' },
    { from: 'ʌ', to: 'V' },
    { from: 'ə̆', to: '@-' }, { from: 'ᵊ', to: '@-' },
    { from: 'ə', to: '@' },
    { from: 'y', to: 'y' },
    { from: 'ʏ', to: 'Y' },

    // approximations involving modifiers & diacritics need to be run first
    { from: 'lˠ', to: 'L' }, { from: 'lˤ', to: 'L' }, // approximate as [ɫ]
    { from: 'ɻ̩', to: 'P' }, { from: 'ɻ̩ː', to: 'P:' }, { from: 'ɻ̩ˑ', to: 'P2' }, { from: 'ɻ\u0329\u0306', to: 'P0' }, { from: 'ɻ\u0306\u0329', to: 'P0' },
    { from: 'ʀ̥̩', to: 'R#-' },
    { from: 'ʀ̩', to: 'R-' }, { from: 'ʀ̩ː', to: 'R-:' }, { from: 'ʀ̩ˑ', to: 'R-2' }, { from: 'ʀ\u0329\u0306', to: 'R-0' }, { from: 'ʀ\u0306\u0329', to: 'R-0' },

    // clicks
    { from: 'kʘ', to: 'k.' }, { from: 'ɡʘ', to: 'g.' }, { from: 'gʘ', to: 'g.' }, { from: 'ŋʘ', to: "g.g~" }, { from: 'ⁿʘ', to: "g.g~" },
    { from: 'kǀ', to: 'k[' }, { from: 'ɡǀ', to: 'g[' }, { from: 'gǀ', to: 'g[' }, { from: 'ŋǀ', to: "g[g~" }, { from: 'ⁿǀ', to: "g[g~" },
    { from: 'kǁ', to: 'k#' }, { from: 'ɡǁ', to: 'g#' }, { from: 'gǁ', to: 'g#' }, { from: 'ŋǁ', to: "g#g~" }, { from: 'ⁿǁ', to: "g#g~" },
    { from: 'kǃ', to: 'k!' }, { from: 'ɡǃ', to: 'g!' }, { from: 'gǃ', to: 'g!' }, { from: 'ŋǃ', to: "g!g~" }, { from: 'ⁿǃ', to: "g!g~" },
    { from: 'kǂ', to: 'k^' }, { from: 'ɡǂ', to: 'g^' }, { from: 'gǂ', to: 'g^' }, { from: 'ŋǂ', to: "g^g~" }, { from: 'ⁿǂ', to: "g^g~" },
    // diacritics for voiced & nasal
    { from: 'ʘ̬', to: 'g.' }, { from: 'ʘ̃', to: "g.g~" }, { from: 'ʘ̬̃', to: 'g.g~' },
    { from: 'ǀ̬', to: 'g[' }, { from: 'ǀ̃', to: "g[g~" }, { from: 'ǀ̬̃', to: 'g[g~' },
    { from: 'ǁ̬', to: 'g#' }, { from: 'ǁ̃', to: "g#g~" }, { from: 'ǁ̬̃', to: 'g#g~' },
    { from: 'ǃ̬', to: 'g!' }, { from: 'ǃ̃', to: "g!g~" }, { from: 'ǃ̬̃', to: 'g!g~' },
    { from: 'ǂ̬', to: 'g^' }, { from: 'ǂ̃', to: "g^g~" }, { from: 'ǂ̬̃', to: 'g^g~' },
    // approximations - ‼, ʞ, and uvular
    { from: 'k‼', to: 'k!' }, { from: 'ɡ‼', to: 'g!' }, { from: 'g‼', to: 'g!' }, { from: 'ŋ‼', to: "g!g~" }, { from: 'ⁿ‼', to: "g!g~" }, { from: '‼', to: 'k!' },  // ‼ > ǃ
    { from: 'kʞ', to: 'k^' }, { from: 'ɡʞ', to: 'g^' }, { from: 'gʞ', to: 'g^' }, { from: 'ŋʞ', to: "g^g~" }, { from: 'ⁿʞ', to: "g^g~" }, { from: 'ʞ', to: 'k^' },  // ʞ > ǂ
    { from: 'qʘ', to: 'k.' }, { from: 'qǀ', to: 'k[' }, { from: 'qǁ', to: 'k#' }, { from: 'qǃ', to: 'k!' }, { from: 'qǂ', to: 'k^' }, { from: 'q‼', to: 'k!' }, { from: 'qʞ', to: 'k^' },
    { from: 'ɢʘ', to: 'g.' }, { from: 'ɢǀ', to: 'g[' }, { from: 'ɢǁ', to: 'g#' }, { from: 'ɢǃ', to: 'g!' }, { from: 'ɢǂ', to: 'g^' }, { from: 'ɢ‼', to: 'g!' }, { from: 'ɢʞ', to: 'g^' },
    { from: 'ɴʘ', to: 'g.g~' }, { from: 'ɴǀ', to: 'g[g~' }, { from: 'ɴǁ', to: 'g#g~' }, { from: 'ɴǃ', to: 'g!g~' }, { from: 'ɴǂ', to: 'g^g~' }, { from: 'ɴ‼', to: 'g!g~' }, { from: 'ɴʞ', to: 'g^g~' },
    // plain clicks
    { from: 'ʘ', to: 'k.' },
    { from: 'ǀ', to: 'k[' },
    { from: 'ǁ', to: 'k#' },
    { from: 'ǃ', to: 'k!' },
    { from: 'ǂ', to: 'k^' },

    // supported affricates
    { from: 'c͡çʼ', to: 'c+C?' }, { from: 'c͡ç', to: 'c+C' },
    { from: 'd͡ɮʼ', to: 'd+L#?' }, { from: 'd͡ɮ', to: 'd+L#' },
    { from: 'd͡zʼ', to: 'd+z?' }, { from: 'd͡z', to: 'd+z' },
    { from: 'd͡ʒʼ', to: 'd+Z?' }, { from: 'd͡ʒ', to: 'd+Z' }, { from: 'd̠͡ʒʼ', to: 'd+Z?' }, { from: 'd̠͡ʒ', to: 'd+Z' },
    { from: 'd͡ʑʼ', to: 'd+Z;?' }, { from: 'd͡ʑː', to: 'd+Z;:' }, { from: 'd͡ʑ', to: 'd+Z;' },
    { from: 't͡ɬʼ', to: 't+l#?' }, { from: 't͡ɬ', to: 't+l#' },
    { from: 't͡sʼ', to: 't+s?' }, { from: 't͡sʰ', to: 't+s/^' }, { from: 't͡s', to: 't+s' },
    { from: 't͡ʃʼ', to: 't+S?' }, { from: 't͡ʃ', to: 't+S' }, { from: 't̠͡ʃʼ', to: 't+S?' }, { from: 't̠͡ʃ', to: 't+S' },
    { from: 'ʈ͡ʂʼ', to: 't.+s?' }, { from: 'ʈ͡ʂ', to: 't.+s' }, { from: 't͡ʂʼ', to: 't.+s?' }, { from: 't͡ʂ', to: 't.+s' },
    { from: 't͡ɕʼ', to: 't+S;?' }, { from: 't͡ɕː', to: 't+S;:' }, { from: 't͡ɕ', to: 't+S;' },

    // supported consonants
    { from: 'b', to: 'b' },
    { from: 'β', to: 'B' },
    { from: 'ç', to: 'C' },
    { from: 'ɕ', to: 'S;' },
    { from: 'cʼ', to: 'c`' },
    { from: 'd̪', to: 'd[' },
    { from: 'ɖ', to: 'd.' },
    { from: 'ð', to: 'D' },
    { from: 'ᴅ', to: 't#' }, // tap as in GA /ˈpɛˌᴅi/
    { from: 'd', to: 'd' },
    { from: 'f', to: 'f' },
    { from: 'ɡ', to: 'g' },
    { from: 'g', to: 'g' },
    { from: 'h', to: 'h' },
    { from: 'ɧ', to: 'h^' },
    { from: 'ɟ', to: 'J' },
    { from: 'ʝ', to: 'J^' },
    { from: 'j', to: 'j' },
    { from: 'kʰ', to: 'k/^' },
    { from: 'kʼ', to: 'k`' },
    { from: 'k˭', to: 'k/0' }, { from: 'kˑ', to: 'k/0' },
    { from: 'k', to: 'k' },
    { from: 'ɬ', to: 'l#' },
    { from: 'ɮ', to: 'L#' },
    { from: 'ʎ', to: 'l^' },
    { from: 'ɭ', to: 'l.' },
    { from: 'ɫ̩', to: 'L-' }, { from: 'ɫ̩ː', to: 'L-:' }, { from: 'ɫ̩ˑ', to: 'L-2' }, { from: 'ɫ\u0329\u0306', to: 'L-0' }, { from: 'ɫ\u0306\u0329', to: 'L-0' },
    { from: 'ɫ', to: 'L' },
    { from: 'l̩', to: 'l-' }, { from: 'l̩ː', to: 'l-:' }, { from: 'l̩ˑ', to: 'l-2' }, { from: 'l\u0329\u0306', to: 'l-0' }, { from: 'l\u0306\u0329', to: 'l-0' },
    { from: 'l', to: 'l' },
    { from: 'ʍ', to: 'w#' },
    { from: 'm̩', to: 'm-' }, { from: 'm̩ː', to: 'm-:' }, { from: 'm̩ˑ', to: 'm-2' }, { from: 'm\u0329\u0306', to: 'm-0' }, { from: 'm\u0306\u0329', to: 'm-0' },
    { from: 'm', to: 'm' },
    { from: 'n̩', to: 'n-' }, { from: 'n̩ː', to: 'n-:' }, { from: 'n̩ˑ', to: 'n-2' }, { from: 'n\u0329\u0306', to: 'n-0' }, { from: 'n\u0306\u0329', to: 'n-0' },
    { from: 'n̪̩', to: 'n-' }, { from: 'n̪̩ː', to: 'n-:' }, { from: 'n̪̩ˑ', to: 'n-2' }, { from: 'n̪\u0329\u0306', to: 'n-0' }, { from: 'n̪\u0306\u0329', to: 'n-0' },
    { from: 'ɳ', to: 'n.' },
    { from: 'ɲ', to: 'n^' },
    { from: 'ŋ̩', to: 'N-' }, { from: 'ŋ̩ː', to: 'N-:' }, { from: 'ŋ̩ˑ', to: 'N-2' }, { from: 'ŋ\u0329\u0306', to: 'N-0' }, { from: 'ŋ\u0306\u0329', to: 'N-0' },
    { from: 'ŋ', to: 'N' },
    { from: 'n', to: 'n' },
    { from: 'pʰ', to: 'p/^' },
    { from: 'pʼ', to: 'p`' },
    { from: 'p˭', to: 'p/0' }, { from: 'pˑ', to: 'p/0' },
    { from: 'p', to: 'p' },
    { from: 'q', to: 'q' },
    { from: 'ɣ̞', to: 'w^' },
    { from: 'ɣ', to: 'Q' },
    { from: 'ʁ', to: 'Q"' },
    { from: 'r̝', to: 'R2' }, { from: 'r̻', to: 'R2' },
    { from: 'ʀ', to: 'r"' },
    { from: 'ɽ', to: 'r.' },
    { from: 'r̩', to: 'R-' }, { from: 'r̩ː', to: 'R-:' }, { from: 'r̩ˑ', to: 'R-2' }, { from: 'r\u0329\u0306', to: 'R-0' }, { from: 'r\u0306\u0329', to: 'R-0' },
    { from: 'r̥̩', to: 'R#-' },
    { from: 'r̥', to: 'R#' },
    { from: 'ɹ', to: 'r' },
    { from: 'ɹ̩', to: 'P' }, { from: 'ɹ̩ː', to: 'P:' }, { from: 'ɹ̩ˑ', to: 'P2' }, { from: 'ɹ\u0329\u0306', to: 'P0' }, { from: 'ɹ\u0306\u0329', to: 'P0' },
    { from: 'ɾ', to: '*' },
    { from: 'r', to: 'R' },
    { from: 'ʂ', to: 's.' },
    { from: 'ʃ', to: 'S' },
    { from: 's', to: 's' },
    { from: 'ʈ', to: 't.' },
    { from: 't̪', to: 't[' },
    { from: 'tʰ', to: 't/^' },
    { from: 'tʼ', to: 't`' },
    { from: 't', to: 't' },
    { from: 'θ', to: 'T' },
    { from: 'ʋ', to: 'v#' },
    { from: 'v', to: 'v' },
    { from: 'ɰ', to: 'w^' },
    { from: 'w', to: 'w' },
    { from: 'χ', to: 'X' },
    { from: 'x', to: 'x' },
    { from: 'ɥ', to: 'y^' },
    { from: 'ʐ', to: 'z.' },
    { from: 'ʑ', to: 'Z;' },
    { from: 'z', to: 'z' },
    { from: 'ʒ', to: 'Z' },
    { from: 'ʔ', to: '?' },
    { from: 'ʕ', to: '?"' },

    // approximate some sounds with a supported phoneme
    // note: if 'to' includes `;` that's not built-in, need to handle long version, e.g. d:; vs d;:
    { from: 'ɓ', to: 'b' },
    { from: 'ʙ', to: 'bR' }, // approximate as br
    { from: 'ɖ͡ʐ', to: 'd+Z' },  // approximate as [d͡ʒ]
    { from: 'ɗ', to: 'd' },
    { from: 'd̠', to: 'd' },
    { from: 'ȡː', to: 'd:;' }, { from: 'ȡ', to: 'd;' }, // approximate as [dʲ]
    { from: 'ᶑ', to: 'd.' }, // approximate as [ɖ]
    { from: 'ɸ', to: 'f' },
    { from: 'ɠ', to: 'g' },
    { from: 'ɢ̆', to: 'g' },
    { from: 'ɢ', to: 'g' },
    { from: 'ʛ', to: 'g' },
    { from: 'ɦ', to: 'h' },
    { from: 'ħ', to: 'h' },
    { from: 'k͡p', to: 'k/0p/0' }, // approximate as kˑpˑ
    { from: 'k͡𝼄', to: 'k/0x' }, // approximate as kˑx
    { from: 'k͡x', to: 'k/0x' }, // approximate as kˑx
    { from: 'ʄː', to: 'g:;' }, { from: 'ʄ', to: 'g;' }, // approximate as [ɡʲ]
    { from: 'ɟ͡ʝː', to: 'J:;' }, { from: 'ɟ͡ʝ', to: 'J;' },  // approximate as [ɟʲ]
    { from: 'l̊', to: 'l' },
    { from: 'ȴː', to: 'l:;' }, { from: 'ȴ', to: 'l;' }, // approximate as [lʲ]
    { from: 'ʟ', to: 'l' },
    { from: 'ꞎ', to: 'l#' },  // approximate as ɬ
    { from: '𝼅', to: 'L#' },  // approximate as ɮ
    { from: '𝼄', to: 'x' },
    { from: 'ʟ̝', to: 'Q' },  // approximate as ɣ
    { from: 'ɮ', to: 'lZ' }, // approximate as lʒ
    { from: '𝼆', to: 'C' }, { from: 'ʎ̥̝', to: 'C' }, // approximate as ç
    { from: 'm̥', to: 'hm' },
    { from: 'ɱ̥', to: 'hm' },
    { from: 'ɱ', to: 'm' },
    { from: 'n̥', to: 'hn' },
    { from: 'ɳ̥', to: 'hn.' },
    { from: 'ŋ̥', to: 'hN' },
    { from: 'ɴ', to: 'N' },  // approximate as ŋ
    { from: 'ȵː', to: 'n:;' }, { from: 'ȵ', to: 'n;' }, // approximate as [nʲ]
    { from: 'ʕ', to: 'Q"' }, // approximate as ʁ
    { from: 'ɻ', to: 'r' },
    { from: 'ɺ', to: 'l' },
    { from: 'ʜ', to: 'hr"' }, // approximate as hʀ
    { from: 'ʢ', to: 'hr"' }, // approximate as hʀ
    { from: '𝼋', to: 'S' }, // approximate as ʃ
    { from: 'ʆ', to: 'S;' },  // approximate as ɕ/ʃʲ
    { from: 't̪ʼ', to: 't[?' },// approximate as t̪ʔ
    { from: 't̠', to: 't' },
    { from: 'ȶː', to: 't:;' }, { from: 'ȶ', to: 't;' },  // approximate as [tʲ]
    { from: 'ⱱ', to: 'v' },
    { from: 'ẘ', to: 'w#', next: { not: '#word' } },
    { from: 'ʓ', to: 'Z;' },  // approximate as ʑ/ʒʲ
    { from: 'n̪', to: 'n' }, { from: 'r̪', to: 'R' },
    { from: 'ɾ̪', to: '*' }, { from: 'l̪', to: 'l' },
    { from: 'ʡ̆', to: 'q' },
    { from: 'ʡ͡ʜ', to: 'qh0' },  // approximate as [qʰ]
    { from: 'ʡ͡ʢ', to: 'q' },
    { from: 'ʡ', to: 'q?' },
    // PIE
    { from: 'h₁', to: 'h' },
    { from: 'h₂', to: 'X' },  // approximate as χ
    { from: 'h₃', to: 'Q' }, // approximate as ɣ
    { from: 'hₓ', to: 'hx' },

    // modifiers not handled by other rules
    // note: /ʴ/ handled by 'preprocess' & /ʲ/ handled earlier
    { from: 'ᵝ', to: 'B' }, // approximated as β
    { from: 'ʰ', to: 'h0' },  // aspiration
    { from: 'ʱ', to: 'h0' },
    { from: 'ᶣ', to: 'y^' }, // approximated as [ɥ]
    { from: 'ˠ', to: 'Q' }, // velarization [ɣ]
    { from: 'ˡ', to: '' },
    { from: 'ⁿ', to: '' },  // nasal
    { from: 'ʳ', to: 'r' },
    { from: 'ᶹ', to: 'v#' }, // approximated as ʋ
    { from: 'ˤ', to: '?"' }, { from: 'ˁ', to: '?"' }, // pharyngealization as [ʕ]
    { from: 'ˀ', to: '?' }, // glottalization as [ʔ]

    // stress & breaks (syllable breaks handled earlier)
    { from: 'ˈˈ', to: "'!" }, // (primary stress, emphasized)
    { from: 'ˈ', to: "'" },   // (primary stress)
    { from: 'ˌ', to: ',' },   // (secondary stress)
    { from: '|ː', to: '_-' }, // (medium break)
    { from: '|', to: '_,' },  // (minor break)
    { from: '‖', to: '_.' },  // (major break)

    // tones (triple combos)
    { from: '˨˦˨', to: 'K^' },  // 242
    { from: '˦˨˦', to: 'K#' },  // 424
    { from: '˨˩˦', to: 'K+4' }, // 214
    // tones (double combos)
    { from: '˦˨', to: 'K6' },  // 42
    { from: '˨˦', to: 'K7' },  // 24
    { from: '˥˩', to: 'K8' },  // 51
    { from: '˩˥', to: 'K9' },  // 15
    { from: '˨˧', to: 'K+2' },  // 23
    { from: '˧˨', to: 'K+3' },  // 32
    { from: '˧˥', to: 'K+5' },  // 35
    // tones (one level)
    { from: '˩', to: 'K1' },
    { from: '˨', to: 'K2' },
    { from: '˧', to: 'K3' },
    { from: '˦', to: 'K4' },
    { from: '˥', to: 'K5' },
  ]
};


const [ipaCompiled, ipaFailed] = compileRuleSet(ipaRules);
